import { useState, useEffect } from 'react';
import Card from '../../../components/base/Card';
import Button from '../../../components/base/Button';

export default function Services() {
  const [currentBg, setCurrentBg] = useState(0);
  const [animatedIndex, setAnimatedIndex] = useState(0);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedService, setSelectedService] = useState<string>('');

  const backgroundImages = [
    'https://readdy.ai/api/search-image?query=beautiful%20airplane%20flying%20through%20vibrant%20sunset%2C%20dynamic%20aviation%20scene%2C%20commercial%20aircraft%20soaring%20majestically%2C%20colorful%20sky%20gradient%2C%20inspiring%20travel%20background%20with%20dramatic%20lighting&width=1920&height=800&seq=services-bg1&orientation=landscape',
    'https://readdy.ai/api/search-image?query=stunning%20aerial%20view%20of%20tropical%20paradise%20islands%20from%20airplane%20window%2C%20turquoise%20blue%20ocean%20waters%2C%20white%20sandy%20beaches%2C%20colorful%20coral%20reefs%20visible%20below%2C%20breathtaking%20travel%20destination%20photography&width=1920&height=800&seq=services-bg2&orientation=landscape',
    'https://readdy.ai/api/search-image?query=magnificent%20mountain%20ranges%20and%20valleys%20viewed%20from%20aircraft%2C%20snow-capped%20peaks%2C%20lush%20green%20landscapes%2C%20dramatic%20scenery%2C%20adventure%20travel%20photography%2C%20inspiring%20high-altitude%20destination%20views&width=1920&height=800&seq=services-bg3&orientation=landscape'
  ];

  const services = [
    {
      title: 'Domestic Flights',
      description: 'Best rates guaranteed on all domestic flights across India with instant booking confirmation.',
      icon: 'ri-flight-takeoff-line',
      gradient: 'from-blue-500 to-cyan-400',
      bgGradient: 'from-blue-50 to-cyan-50',
      features: ['Lowest Price Promise', 'Instant Confirmation', 'Free Cancellation', 'All Airlines Available'],
      badge: 'CORE SERVICE',
      isCore: true,
      delay: 0
    },
    {
      title: 'International Flights',
      description: 'Unbeatable deals on international flights to worldwide destinations with flexible booking options.',
      icon: 'ri-flight-land-line',
      gradient: 'from-purple-500 to-pink-400',
      bgGradient: 'from-purple-50 to-pink-50',
      features: ['Global Destinations', 'Multi-City Options', 'Group Discounts', 'Visa Assistance'],
      badge: 'CORE SERVICE',
      isCore: true,
      delay: 200
    },
    {
      title: 'Hotel Bookings',
      description: 'Premium accommodations from luxury resorts to budget-friendly stays across India.',
      icon: 'ri-hotel-bed-line',
      gradient: 'from-emerald-500 to-teal-400',
      bgGradient: 'from-emerald-50 to-teal-50',
      features: ['5-Star Hotels', 'Heritage Properties', 'Beach Resorts', 'Hill Station Hotels'],
      badge: 'POPULAR',
      isCore: false,
      delay: 400
    },
    {
      title: 'Car Rentals',
      description: 'Comfortable transportation with experienced drivers for all your travel needs.',
      icon: 'ri-car-line',
      gradient: 'from-orange-500 to-red-400',
      bgGradient: 'from-orange-50 to-red-50',
      features: ['Luxury Sedans', 'SUVs Available', 'Chauffeur Service', 'Airport Transfers'],
      badge: 'TRENDING',
      isCore: false,
      delay: 600
    },
    {
      title: 'Visa Services',
      description: 'Complete visa assistance and documentation support for hassle-free travel.',
      icon: 'ri-passport-line',
      gradient: 'from-indigo-500 to-blue-400',
      bgGradient: 'from-indigo-50 to-blue-50',
      features: ['Tourist Visas', 'Business Visas', 'Express Processing', 'Document Help'],
      badge: 'ESSENTIAL',
      isCore: false,
      delay: 800
    },
    {
      title: 'Tour Packages',
      description: 'Complete travel solutions combining flights, hotels, and experiences at best rates.',
      icon: 'ri-map-pin-line',
      gradient: 'from-pink-500 to-rose-400',
      bgGradient: 'from-pink-50 to-rose-50',
      features: ['Flight + Hotel', 'Customized Itineraries', 'Local Experiences', 'Group Packages'],
      badge: 'COMPLETE',
      isCore: false,
      delay: 1000
    }
  ];

  // Auto change background every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBg((prev) => (prev + 1) % backgroundImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Animate cards sequentially
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimatedIndex((prev) => (prev + 1) % services.length);
    }, 1500);
    return () => clearInterval(interval);
  }, []);

  const handleServiceClick = (title: string) => {
    if (title === 'Hotel Bookings') {
      const servicesSection = document.getElementById('services');
      if (servicesSection) {
        servicesSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleBookNow = (serviceTitle: string) => {
    setSelectedService(serviceTitle);
    setShowBookingModal(true);
  };

  const handleCallNow = () => {
    const message = "Hi MaxJourneys! I'm interested in your flight booking services. Please provide me with the best rates and availability. Thank you!";
    const whatsappUrl = `https://wa.me/919700644857?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleQuickContact = () => {
    const message = `Hi MaxJourneys! I'm interested in booking ${selectedService}. Please provide me with the best rates and availability. Thank you!`;
    const whatsappUrl = `https://wa.me/919700644857?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleEmailContact = () => {
    const subject = `${selectedService} Booking Inquiry - MaxJourneys`;
    const body = `Dear MaxJourneys Team,

I am interested in booking ${selectedService} and would like to know more about:

- Available options and pricing
- Best deals and offers
- Booking process and requirements
- Any special packages or discounts

Please contact me with the best rates and availability.

Thank you for your time.

Best regards`;
    
    const mailtoUrl = `mailto:info@maxjourneys.in?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoUrl, '_blank');
    
    // Show confirmation message
    alert('Email client opened! If it didn\'t open automatically, please email us at info@maxjourneys.in');
  };

  return (
    <>
      <section 
        id="services" 
        className="py-20 relative overflow-hidden transition-all duration-1000"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 51, 234, 0.1) 25%, rgba(236, 72, 153, 0.1) 50%, rgba(245, 101, 101, 0.1) 75%, rgba(34, 197, 94, 0.1) 100%), url('${backgroundImages[currentBg]}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        {/* Simplified animated overlay patterns */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full">
            <div className="animate-pulse absolute top-20 left-20 w-16 h-16 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full blur-xl"></div>
            <div className="animate-bounce absolute top-40 right-32 w-12 h-12 bg-gradient-to-r from-pink-400 to-red-400 rounded-full blur-lg" style={{ animationDelay: '1s' }}></div>
            <div className="animate-pulse absolute bottom-32 left-1/4 w-20 h-20 bg-gradient-to-r from-green-400 to-blue-400 rounded-full blur-xl" style={{ animationDelay: '2s' }}></div>
            <div className="animate-bounce absolute bottom-20 right-1/3 w-14 h-14 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full blur-lg" style={{ animationDelay: '3s' }}></div>
          </div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full text-sm font-bold mb-6 shadow-lg">
              <i className="ri-flight-takeoff-line mr-2"></i>
              India's Leading Flight Booking Platform
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              Unbeatable Flight Deals
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed font-medium">
              Experience the best flight booking service in India with guaranteed lowest prices, instant confirmations, and comprehensive travel solutions for all your domestic and international travel needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className={`transform transition-all duration-700 hover:scale-105 ${
                  animatedIndex === index ? 'animate-pulse' : ''
                }`}
                style={{ animationDelay: `${service.delay}ms` }}
              >
                <Card 
                  hover 
                  className={`text-center relative overflow-hidden bg-gradient-to-br ${service.bgGradient} border-2 border-transparent hover:border-gradient-to-r hover:${service.gradient} group cursor-pointer h-full`}
                  onClick={() => handleServiceClick(service.title)}
                >
                  {/* Animated background effect */}
                  <div className={`absolute inset-0 bg-gradient-to-r ${service.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
                  
                  {/* Fixed floating badge - improved positioning */}
                  {service.badge && (
                    <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 z-20">
                      <span className={`bg-gradient-to-r ${service.gradient} text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg whitespace-nowrap`}>
                        {service.badge}
                      </span>
                    </div>
                  )}

                  <div className="p-6 relative z-10 h-full flex flex-col justify-between min-h-[420px]">
                    {/* Top section with icon and title */}
                    <div className="flex-shrink-0 pt-4">
                      {/* Animated icon container */}
                      <div className={`w-16 h-16 flex items-center justify-center bg-gradient-to-r ${service.gradient} rounded-full mx-auto mb-4 shadow-xl transform group-hover:rotate-12 transition-transform duration-500 group-hover:scale-110`}>
                        <i className={`${service.icon} text-2xl text-white`}></i>
                      </div>

                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-purple-600 transition-all duration-300 leading-tight">
                        {service.title}
                      </h3>
                    </div>

                    {/* Middle section with description and features */}
                    <div className="flex-grow">
                      <p className="text-gray-600 mb-4 leading-relaxed font-medium text-sm">
                        {service.description}
                      </p>

                      {/* Feature list */}
                      <div className="space-y-2 mb-6">
                        {service.features.map((feature, featureIndex) => (
                          <div 
                            key={featureIndex}
                            className="flex items-center justify-start transform hover:translate-x-1 transition-transform duration-300"
                            style={{ animationDelay: `${featureIndex * 100}ms` }}
                          >
                            <i className="ri-check-line text-green-500 mr-2 text-sm flex-shrink-0"></i>
                            <span className="text-gray-700 text-xs font-medium text-left">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Bottom section with button */}
                    <div className="flex-shrink-0">
                      <Button 
                        className={`w-full bg-gradient-to-r ${service.gradient} hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 text-white font-bold py-3 text-sm`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (service.isCore) {
                            handleBookNow(service.title);
                          } else {
                            handleServiceClick(service.title);
                          }
                        }}
                      >
                        {service.isCore ? (
                          <>
                            <i className="ri-rocket-line mr-2"></i>
                            Book Now
                          </>
                        ) : (
                          <>
                            <i className="ri-arrow-right-line mr-2 group-hover:translate-x-1 transition-transform duration-300"></i>
                            Learn More
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Hover glow effect */}
                  <div className={`absolute inset-0 bg-gradient-to-r ${service.gradient} opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 -z-10`}></div>
                </Card>
              </div>
            ))}
          </div>

          {/* Enhanced CTA section */}
          <div className="text-center mt-16">
            <div className="bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 p-1 rounded-2xl shadow-2xl max-w-md mx-auto">
              <div className="bg-white rounded-xl p-8">
                <div className="flex items-center justify-center mb-4">
                  <div className="animate-pulse w-4 h-4 bg-green-500 rounded-full mr-2"></div>
                  <span className="text-green-600 font-bold text-sm">Live Support Available</span>
                </div>
                <h3 className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent mb-4">
                  Get Instant Flight Quotes!
                </h3>
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-orange-500 to-pink-600 hover:from-orange-600 hover:to-pink-700 transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl"
                  onClick={handleCallNow}
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call Now - Best Rates Guaranteed!
                </Button>
                <p className="text-xs text-gray-500 mt-3">
                  Available 24/7 • Instant Quotes • No Hidden Fees
                </p>
              </div>
            </div>
          </div>

          {/* Floating particles effect - reduced and optimized */}
          <div className="absolute inset-0 pointer-events-none opacity-30">
            <div className="animate-float absolute top-1/4 left-1/4 w-2 h-2 bg-blue-400 rounded-full" style={{ animationDelay: '0s', animationDuration: '4s' }}></div>
            <div className="animate-float absolute top-1/3 right-1/4 w-2 h-2 bg-purple-400 rounded-full" style={{ animationDelay: '1s', animationDuration: '5s' }}></div>
            <div className="animate-float absolute bottom-1/4 left-1/3 w-2 h-2 bg-pink-400 rounded-full" style={{ animationDelay: '2s', animationDuration: '6s' }}></div>
            <div className="animate-float absolute bottom-1/3 right-1/3 w-1 h-1 bg-yellow-400 rounded-full" style={{ animationDelay: '3s', animationDuration: '7s' }}></div>
          </div>
        </div>

        {/* Custom CSS for floating animation */}
        <style jsx>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.6; }
            33% { transform: translateY(-15px) rotate(120deg); opacity: 1; }
            66% { transform: translateY(-8px) rotate(240deg); opacity: 0.8; }
          }
          .animate-float {
            animation: float ease-in-out infinite;
          }
        `}</style>
      </section>

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b px-8 py-6 flex justify-between items-center">
              <div className="flex items-center">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mr-4">
                  <i className="ri-flight-takeoff-line text-2xl text-white"></i>
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">Book {selectedService}</h2>
                  <p className="text-gray-600">Get the best deals instantly!</p>
                </div>
              </div>
              <button
                onClick={() => setShowBookingModal(false)}
                className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-full hover:bg-gray-100 transition-colors"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            
            <div className="p-8">
              <div className="text-center mb-8">
                <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Ready to Book Your {selectedService}?</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="text-center">
                      <i className="ri-price-tag-3-line text-3xl text-green-600 mb-2"></i>
                      <p className="font-bold text-gray-900">Best Price</p>
                      <p className="text-sm text-gray-600">Guaranteed lowest rates</p>
                    </div>
                    <div className="text-center">
                      <i className="ri-time-line text-3xl text-blue-600 mb-2"></i>
                      <p className="font-bold text-gray-900">Instant Booking</p>
                      <p className="text-sm text-gray-600">Immediate confirmation</p>
                    </div>
                    <div className="text-center">
                      <i className="ri-customer-service-2-line text-3xl text-purple-600 mb-2"></i>
                      <p className="font-bold text-gray-900">24/7 Support</p>
                      <p className="text-sm text-gray-600">Always here to help</p>
                    </div>
                  </div>
                  <p className="text-gray-700 text-lg leading-relaxed">
                    Contact our travel experts to get personalized quotes and book your {selectedService.toLowerCase()} at the best available rates. We're available 24/7 to assist you!
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-blue-50 p-6 rounded-lg">
                    <i className="ri-phone-line text-3xl text-blue-600 mb-3"></i>
                    <h4 className="text-lg font-bold mb-2">Call for Instant Booking</h4>
                    <p className="text-gray-600 mb-4">Speak directly with our travel experts</p>
                    <Button 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      onClick={handleCallNow}
                    >
                      <i className="ri-phone-line mr-2"></i>
                      Call +91 9700644857
                    </Button>
                  </div>
                  <div className="bg-green-50 p-6 rounded-lg">
                    <i className="ri-chat-3-line text-3xl text-green-600 mb-3"></i>
                    <h4 className="text-lg font-bold mb-2">WhatsApp Quick Quote</h4>
                    <p className="text-gray-600 mb-4">Get instant response on WhatsApp</p>
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={handleQuickContact}
                    >
                      <i className="ri-whatsapp-line mr-2"></i>
                      WhatsApp Now
                    </Button>
                  </div>
                </div>

                <div className="bg-orange-50 p-6 rounded-lg">
                  <i className="ri-mail-line text-3xl text-orange-600 mb-3"></i>
                  <h4 className="text-lg font-bold mb-2">Email for Detailed Quote</h4>
                  <p className="text-gray-600 mb-4">Get comprehensive travel options via email</p>
                  <Button 
                    className="bg-orange-600 hover:bg-orange-700"
                    onClick={handleEmailContact}
                  >
                    <i className="ri-mail-line mr-2"></i>
                    Send Email Inquiry
                  </Button>
                </div>

                <div className="mt-8 p-6 bg-yellow-50 rounded-lg">
                  <div className="flex items-center justify-center mb-3">
                    <i className="ri-time-line text-yellow-600 mr-2"></i>
                    <span className="font-bold text-yellow-800">Business Hours: 24/7 Available</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Our travel experts are available round the clock to help you with the best deals and instant bookings.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
